import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useState } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
  Validate,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import CommonTextField from "../../../../common/components/functional/commonTextField/CommonTextField";
import DialogComponent from "../../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import isEmpty from "../../../../common/helpers/isEmpty";
import { VerticalAlignGrid } from "../../../invoices/styled";
import {
  createNewCustomerUser,
  selectAllCustomerUsers,
} from "../../customerUsersSlice";
import { CreateCustomerUser } from "../../types/commands";
import { CustomerContact } from "../../types/types";
import UserForm from "../../users/UserForm";

interface Option {
  id: string;
  name: string;
}

interface Props {
  label: string;
  fieldName: string;
  customerCompanyId: string;
  options?: Option[];
}

const LabelAutocompleteSelectRow = ({
  label,
  fieldName,
  customerCompanyId,
  options = [],
}: Props) => {
  const [modalState, setModalState] = useState<{ open: boolean }>({
    open: false,
  });
  const { control, setValue } = useFormContext();

  const { t } = useTranslation("customers");
  const dispatch = useAppDispatch();
  const customerUsers = useAppSelector(selectAllCustomerUsers);

  const defaultValues: CustomerContact = {
    firstName: "",
    lastName: "",
    phone: "",
    emailAddress: "",
    title: "",
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const onClose = () => setModalState({ open: false });
  const onOpen = () => setModalState({ open: true });

  const onSave = (customerContact: CustomerContact) => {
    const customerUser: CreateCustomerUser = {
      ...customerContact,
      customerCompanyId,
    };

    dispatch(createNewCustomerUser(customerUser)).then((action) => {
      if (isFulfilled(action)) {
        const customerUserCreated = action.payload.find(
          (event) => event.name === "custUsrCreated",
        );

        if (customerUserCreated) {
          const formattedCustomerUser = {
            id: customerUserCreated.id,
            name: `${customerUserCreated.payload.firstName} ${customerUserCreated.payload.lastName}`,
          };

          setValue(fieldName, formattedCustomerUser, {
            shouldDirty: true,
          });
          onClose();
        }
      }
    });
  };

  const validateEmailAddress: Validate<string, FieldValues> = (
    emailAddress: string,
  ) => {
    const isDirty = !!methods.formState.dirtyFields["emailAddress"];

    if (!isDirty) return true;

    // TODO: move validation to backend
    const isExistingContact = customerUsers.find(
      (customerUser) => customerUser.account.emailAddress === emailAddress,
    );

    if (isExistingContact) {
      const emailInUse = t("customers:usersNS:emailInUse");
      return emailInUse;
    }

    return true;
  };

  const saveDisabled =
    !isEmpty(methods.formState.errors) ||
    !methods.formState.isValid ||
    !methods.formState.isDirty;

  const noOptionsComponent = (
    <>
      <Typography>{t("contract.personNotFound")}</Typography>
      <Button onClick={() => onOpen()}>{t("contract.addPerson")}</Button>
    </>
  );

  return (
    <VerticalAlignGrid container spacing={1}>
      <Grid
        container
        item
        xs={3}
        justifyContent="flex-end"
        paddingRight="0.5rem"
        textAlign="end"
      >
        <Typography variant="secondary">{label}</Typography>
      </Grid>
      <Grid item xs={5}>
        <FormControl fullWidth size="small">
          <Controller
            name={fieldName}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                value={value}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, selectedValue) =>
                  option.id === selectedValue.id
                }
                onChange={(_, newValue) => onChange(newValue)}
                options={options}
                renderInput={(params) => (
                  <CommonTextField
                    {...params}
                    label={!value ? t("contract.selectContactPerson") : ""}
                  />
                )}
                noOptionsText={noOptionsComponent}
              />
            )}
          />
        </FormControl>
      </Grid>
      <DialogComponent open={modalState.open} onClose={onClose}>
        <DialogContentWrapper
          margin
          title={t("customers:workLocationNS:addCustomerContactPerson")}
          onCancel={onClose}
          onSave={methods.handleSubmit(onSave)}
          saveDisabled={saveDisabled}
        >
          <FormProvider {...methods}>
            <UserForm
              validateEmailAddress={validateEmailAddress}
              disablePrefill
            />
          </FormProvider>
        </DialogContentWrapper>
      </DialogComponent>
    </VerticalAlignGrid>
  );
};

export default LabelAutocompleteSelectRow;
